import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFacebook, FaPinterest } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import useJobStore from "../store/jobStore";
import ImageViewer from "../components/ImageViewer";
// Removed useAuthStore as it's likely not needed here unless for specific checks

const ResultsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { jobId, jobStatus, resultImage, resetJobState } = useJobStore();

  // Get original image from location state
  const originalImage = location.state?.originalImage as string | undefined;

  // State for fullscreen image viewer
  const [showImageViewer, setShowImageViewer] = useState(false);

  // Reset the workflow - Navigates back to home (style selection)
  const handleReset = () => {
    if (typeof jobId === "string") {
      window.trackEvent?.("flow_reset");
    }
    resetJobState(); // Reset store state
    navigate("/home");
  };

  // Open the fullscreen image viewer
  const handleViewImage = () => {
    if (resultImage) {
      setShowImageViewer(true);
    }
  };

  // Close the image viewer
  const handleCloseViewer = () => {
    setShowImageViewer(false);
  };

  // Handle social media sharing
  const handleShare = (platform: "facebook" | "x" | "pinterest") => {
    if (!resultImage) {
      console.error("Result image URL is not available for sharing.");
      return;
    }
    // Ensure jobId is a string before tracking
    if (typeof jobId === "string") {
      window.trackEvent?.("image_shared", { platform, jobId }); // Include jobId
    }

    const urlToShare = encodeURIComponent(resultImage);
    const text = encodeURIComponent(t("homePage.share.defaultText")); // Reuse existing key
    let shareUrl = "";

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${urlToShare}`;
        break;
      case "x":
        shareUrl = `https://x.com/intent/tweet?url=${urlToShare}&text=${text}`;
        break;
      case "pinterest":
        shareUrl = `https://pinterest.com/pin/create/button/?url=${urlToShare}&media=${urlToShare}&description=${text}`;
        break;
      default:
        console.error("Unsupported share platform:", platform);
        return;
    }
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=450");
  };

  // Track job completion/failure/errors based on jobStatus changes
  useEffect(() => {
    // Ensure jobId is available for tracking context, though some errors might occur before jobId is set
    const trackingJobId = typeof jobId === "string" ? jobId : "unknown";

    if (jobStatus?.status === "completed") {
      // Check if already tracked might be needed if status updates multiple times
      window.trackEvent?.("job_completed", { jobId: trackingJobId });
    } else if (jobStatus?.status === "failed") {
      // Track definitive job failure
      window.trackEvent?.("job_failed", {
        jobId: trackingJobId,
        reason: jobStatus.error || "unknown_failure", // Ensure a reason is always sent
      });
    } else if (jobStatus?.error) {
      // Track errors that might occur *before* the status is 'failed'
      // or if the status doesn't become 'failed' (e.g., connection errors)
      // The preceding checks ensure status is not 'failed' or 'completed' here.
      window.trackEvent?.("job_error_displayed", {
        jobId: trackingJobId,
        reason: jobStatus.error, // Send the specific error key/message
      });
    }
  }, [jobStatus, jobId]); // Depend on the entire jobStatus object

  // Render different UI based on jobStatus
  const renderContent = () => {
    // Loading state (before jobStatus is available or while processing)
    if (
      !jobStatus ||
      (jobStatus.status !== "completed" && jobStatus.status !== "failed")
    ) {
      return (
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-500 bg-[size:200%_auto] animate-gradient-x">
            {t("homePage.processing.title")} {/* Reuse key */}
          </h2>
          <div className="flex justify-center items-center mb-6">
            {/* Reusing the SVG spinner */}
            <svg
              width="80"
              height="80"
              viewBox="0 0 44 44"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#9CA3AF"
            >
              <g fill="none" fillRule="evenodd" strokeWidth="2">
                <circle cx="22" cy="22" r="1">
                  <animate
                    attributeName="r"
                    begin="0s"
                    dur="1.8s"
                    values="1; 20"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.165, 0.84, 0.44, 1"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="stroke-opacity"
                    begin="0s"
                    dur="1.8s"
                    values="1; 0"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.3, 0.61, 0.355, 1"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle cx="22" cy="22" r="1">
                  <animate
                    attributeName="r"
                    begin="-0.9s"
                    dur="1.8s"
                    values="1; 20"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.165, 0.84, 0.44, 1"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="stroke-opacity"
                    begin="-0.9s"
                    dur="1.8s"
                    values="1; 0"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.3, 0.61, 0.355, 1"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            </svg>
          </div>
        </div>
      );
    }

    // Failed state
    if (jobStatus.status === "failed") {
      return (
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-6 text-red-500">
            {t("resultsPage.failed.title", "Processing Failed")}
          </h2>
          <div className="mb-6 p-4 bg-red-900 bg-opacity-50 text-red-300 rounded-lg border border-red-800">
            {t("homePage.processing.failedPrefix")} {/* Reuse key */}
            {/* Use jobStatus.error for the specific reason */}
            {jobStatus.error
              ? t(`errors.${jobStatus.error}`, jobStatus.error) // Attempt translation of error code
              : t("homePage.processing.unknownError")}{" "}
            {/* Reuse key */}
          </div>
          <button
            onClick={handleReset} // Reset button navigates home
            className="px-6 py-3 text-white rounded-full hover:opacity-90 font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            style={{
              background: "linear-gradient(to right, #6366F1, #8B5CF6)",
              border: "none",
            }}
          >
            {t("homePage.processing.tryAgainButton")} {/* Reuse key */}
          </button>
        </div>
      );
    }

    // Completed state
    if (jobStatus.status === "completed" && resultImage) {
      return (
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-cyan-500 to-blue-500">
            {t("resultsPage.completed.title", "Your Image is Ready!")}
          </h2>
          {/* Container for side-by-side images */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            {/* Original Image */}
            {originalImage && (
              <div className="text-center">
                <img
                  src={originalImage}
                  alt={t(
                    "resultsPage.originalImageAlt",
                    "Original uploaded image"
                  )}
                  className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
                />
              </div>
            )}

            {/* Result Image */}
            <div className="text-center">
              <img
                src={resultImage}
                alt={t("homePage.result.imageAlt")} // Reuse key
                className={`max-w-full max-h-96 mx-auto rounded-lg shadow-lg cursor-pointer hover:opacity-90 transition-opacity ${
                  !originalImage ? "md:col-span-2" : ""
                }`}
                onClick={handleViewImage}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleReset} // Reset goes home
              className="px-6 py-3 text-white rounded-full hover:opacity-90 font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              style={{
                background: "linear-gradient(to right, #6366F1, #8B5CF6)",
                border: "none",
              }}
            >
              {t("homePage.result.processAnotherButton")} {/* Reuse key */}
            </button>
            <button
              onClick={() => {
                navigate("/my-images");
                window.trackEvent?.("view_all_images_clicked");
              }}
              className="px-6 py-3 text-white rounded-full hover:bg-gray-600 font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              {t("homePage.result.viewAllButton")} {/* Reuse key */}
            </button>
          </div>
          {/* Social Sharing Icons */}
          <div className="mt-8 flex justify-center space-x-6">
            <button
              onClick={() => handleShare("facebook")}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label={t("homePage.share.facebook")}
            >
              <FaFacebook size={28} />
            </button>
            <button
              onClick={() => handleShare("x")}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label={t("homePage.share.x")}
            >
              <FaXTwitter size={28} />
            </button>
            <button
              onClick={() => handleShare("pinterest")}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label={t("homePage.share.pinterest")}
            >
              <FaPinterest size={28} />
            </button>
          </div>
        </div>
      );
    }

    // Fallback if state is unexpected (e.g., completed but no resultImage)
    // This indicates a potential logic error or unexpected state from the store
    window.trackEvent?.("job_unexpected_state", {
      jobId: typeof jobId === "string" ? jobId : "unknown",
      status: jobStatus?.status ?? "unknown",
      hasResultImage: String(!!resultImage), // Convert boolean to string
    });
    return (
      <div className="text-center text-gray-400">
        <p>
          {t(
            "resultsPage.unexpectedState",
            "Something went wrong while displaying the results."
          )}
        </p>
        <button
          onClick={handleReset}
          className="mt-4 text-indigo-400 hover:text-indigo-300"
        >
          {t("resultsPage.backToHome", "Go back home")}
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="w-full max-w-4xl mx-auto py-12 px-4">
        {renderContent()}
      </div>

      {/* Fullscreen Image Viewer Modal */}
      {showImageViewer && resultImage && (
        <ImageViewer
          image={{ id: jobId || "result-fallback", url: resultImage }} // Use jobId or fallback for id
          onClose={handleCloseViewer}
          showNavigation={false} // Assuming no navigation needed for single result view
        />
      )}
    </>
  );
};

export default ResultsPage;
